import type { Json, TablesInsert, TablesUpdate } from '@powerful/types';

import { getSupabaseClient } from '../supabase';

type BusinessInsert = TablesInsert<'businesses'>;
type BusinessUpdate = TablesUpdate<'businesses'>;

/**
 * Rascunho do onboarding profissional (T1.15, movido na T2.0).
 *
 * 🔴 **Duas escritas diferentes, e a diferença importa.** As etapas 1, 3 e 5 preenchem colunas
 * reais de `businesses`. O resto — serviços e agenda, cujas tabelas só nascem no M2/M3 — vai para
 * `business_onboarding_drafts`, e a T2.3/T3.1 materializam-no. Descartá-lo obrigaria a repetir um
 * onboarding de 25–45 min.
 *
 * 🔴 **Nunca `businesses.draft_payload`** (T2.0): a coluna era legível por anon em negócios ativos.
 * Foi contraída na T2.0b; a tabela nova é só do dono.
 *
 * A coluna `step` do rascunho guarda a etapa mais avançada alcançada. É isso que permite retomar
 * onde se parou — sem ela, sair e voltar recomeçaria do zero, que é onde as pessoas abandonam.
 *
 * Como em `lib/auth/`, nada lança: o erro volta como valor.
 */

export type BusinessDraftErrorCode =
  | 'session_expired'
  | 'validation_error'
  | 'save_failed'
  | 'network';

export type BusinessDraftResult<T> =
  | { ok: true; data: T }
  | { ok: false; code: BusinessDraftErrorCode; message: string };

export interface BusinessDraft {
  businessId: string;
  step: number;
  name: string | null;
  nif: string | null;
  payload: Record<string, unknown>;
}

const TOTAL_ETAPAS = 7;

/** Colunas reais de `businesses` que cada etapa pode escrever. */
const COLUNAS_POR_ETAPA: Record<number, readonly string[]> = {
  1: ['name', 'nif', 'primary_category_id'],
  2: [], // `services` nasce no M2 — vai inteira para o rascunho.
  3: ['address_line', 'city', 'postal_code', 'amenities'],
  4: [], // `business_hours` nasce no M2/M3 — idem.
  5: ['current_plan'],
  6: [], // A identidade vive em `trader_verifications`/`stripe_connect_accounts`.
  7: [],
};

const falha = (code: BusinessDraftErrorCode, message: string): BusinessDraftResult<never> => ({
  ok: false,
  code,
  message,
});

async function utilizadorAtual(): Promise<string | null> {
  const { data } = await getSupabaseClient().auth.getUser();
  return data?.user?.id ?? null;
}

/** Lê o negócio em rascunho do utilizador, se existir. */
export async function loadBusinessDraft(): Promise<BusinessDraftResult<BusinessDraft | null>> {
  try {
    const userId = await utilizadorAtual();
    if (!userId) return falha('session_expired', 'Sessão expirada.');

    const client = getSupabaseClient();
    const { data, error } = await client
      .from('businesses')
      .select('id, name, nif, status')
      .eq('owner_id', userId)
      .maybeSingle();

    // 🔴 Falha de leitura NÃO é "sem rascunho" (T1.19, /code-review): tratá-la assim mostrava a etapa
    // 1 e o avanço seguinte criava um segundo negócio para o mesmo dono.
    if (error) return falha('network', 'Sem ligação. Tente novamente.');

    // Um negócio já ativo não volta ao onboarding: edita-se no painel, não se refaz o fluxo.
    if (!data || data.status !== 'draft') return { ok: true, data: null };

    const { data: rascunho, error: erroRascunho } = await client
      .from('business_onboarding_drafts')
      .select('payload, step')
      .eq('business_id', data.id)
      .maybeSingle();

    // Mesma regra: retomar na etapa 1 por uma falha de rede faria refazer o que já está gravado.
    if (erroRascunho) return falha('network', 'Sem ligação. Tente novamente.');

    return {
      ok: true,
      data: {
        businessId: data.id as string,
        // Sem linha de rascunho, a pessoa ainda não passou da etapa 1.
        step: (rascunho?.step as number | undefined) ?? 1,
        name: (data.name as string | null) ?? null,
        nif: (data.nif as string | null) ?? null,
        payload: (rascunho?.payload as Record<string, unknown> | undefined) ?? {},
      },
    };
  } catch {
    return falha('network', 'Sem ligação. Tente novamente.');
  }
}

export interface SaveStepInput {
  businessId: string | null;
  step: number;
  values: Record<string, unknown>;
}

/** Grava uma etapa. Cria o negócio na primeira, atualiza nas seguintes. */
export async function saveBusinessStep({
  businessId,
  step,
  values,
}: SaveStepInput): Promise<BusinessDraftResult<{ businessId: string }>> {
  if (!Number.isInteger(step) || step < 1 || step > TOTAL_ETAPAS) {
    return falha('validation_error', 'Etapa inválida.');
  }

  try {
    const userId = await utilizadorAtual();
    if (!userId) return falha('session_expired', 'Sessão expirada.');

    const client = getSupabaseClient();
    const colunas = COLUNAS_POR_ETAPA[step] ?? [];

    // O que a etapa escreve em colunas reais.
    const emColunas: Record<string, unknown> = {};
    for (const coluna of colunas) {
      if (values[coluna] !== undefined) emColunas[coluna] = values[coluna];
    }

    // O que sobra — as etapas sem tabela no M1 — vai para o rascunho. `step` nunca entra no
    // payload: vive na coluna, e a constraint `drafts_no_step_key` recusaria a escrita.
    const noRascunho: Record<string, unknown> = {};
    for (const [chave, valor] of Object.entries(values)) {
      if (!colunas.includes(chave) && chave !== 'step') noRascunho[chave] = valor;
    }

    if (!businessId) {
      // Os campos obrigatórios são literais, para o TypeScript os verificar de verdade. Só o
      // que vem de `COLUNAS_POR_ETAPA` entra por spread — são nomes reais de `businesses`, mas
      // o compilador não tem como o saber a partir de um `Record<string, unknown>`.
      const linha: BusinessInsert = {
        owner_id: userId,
        status: 'draft',
        name: String(emColunas.name ?? ''),
        ...(emColunas as Partial<BusinessInsert>),
      };

      const { data, error } = await client.from('businesses').upsert(linha).select().single();
      if (error || !data?.id) return falha('save_failed', 'Não foi possível guardar.');

      // O erro desta escrita é tolerado de propósito: o negócio já existe, e devolver falha faria a
      // página esquecer o `businessId` — o avanço seguinte criaria um SEGUNDO negócio (T1.19). Sem
      // linha de rascunho a retoma é a etapa 1, que é onde a pessoa está; a gravação seguinte a cria.
      await client
        .from('business_onboarding_drafts')
        .upsert({ business_id: data.id, payload: noRascunho as Json, step }, { onConflict: 'business_id' });

      return { ok: true, data: { businessId: data.id as string } };
    }

    // Reescrever o rascunho inteiro apagaria o que as outras etapas guardaram; lê-se o que já lá
    // está e funde-se.
    const { data: atual, error: erroLeitura } = await client
      .from('business_onboarding_drafts')
      .select('payload, step')
      .eq('business_id', businessId)
      .maybeSingle();

    // Sem ler o rascunho não se funde — gravar `{...novo}` apagaria as etapas anteriores (T1.19).
    if (erroLeitura) return falha('save_failed', 'Não foi possível guardar.');

    const anterior = (atual?.payload as Record<string, unknown> | undefined) ?? {};
    const anteriorStep = (atual?.step as number | undefined) ?? 1;

    if (Object.keys(emColunas).length > 0) {
      const { error } = await client.from('businesses').update(emColunas as BusinessUpdate).eq('id', businessId);
      if (error) return falha('save_failed', 'Não foi possível guardar.');
    }

    const { error } = await client.from('business_onboarding_drafts').upsert(
      {
        business_id: businessId,
        payload: { ...anterior, ...noRascunho } as Json,
        // `step` guarda a etapa mais avançada alcançada, não a última visitada: voltar atrás para
        // corrigir o NIF não pode fazer a retoma perder as etapas seguintes.
        step: Math.max(anteriorStep, step),
      },
      { onConflict: 'business_id' },
    );

    if (error) return falha('save_failed', 'Não foi possível guardar.');
    return { ok: true, data: { businessId } };
  } catch {
    return falha('network', 'Sem ligação. Tente novamente.');
  }
}
